import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Form, Button, Row, Col } from "react-bootstrap";
import {
  setContactForm,
  setShowAgentInfo,
  setAgentForm,
  setFormErrors,
  clearFormError,
  fetchPropertyDetails,
} from "../slice";
import { US_STATES } from "app/constants/global-constants";
import { validateEmail } from "utils/helperFunctions/inputValidations";

const StepContactAddress = () => {
  const dispatch = useDispatch();
  const { uniqueId } = useParams();

  const { contactForm, showAgentInfo, agentForm, formErrors, propertyLoading } =
    useSelector((state) => state.publicClientBooking);

  // Build full address for API call
  const fullAddress = useMemo(() => {
    const parts = [
      contactForm.address1,
      contactForm.address2,
      contactForm.city,
      contactForm.state,
      contactForm.postalCode,
    ].filter(Boolean);
    return parts.join(", ");
  }, [contactForm]);

  const handleContactChange = (field, value) => {
    dispatch(setContactForm({ [field]: value }));
    if (formErrors[field]) {
      dispatch(clearFormError(field));
    }
  };

  const handleAgentChange = (field, value) => {
    dispatch(setAgentForm({ [field]: value }));
    if (formErrors[field]) {
      dispatch(clearFormError(field));
    }
  };

  const handleAgentCheckbox = (e) => {
    dispatch(setShowAgentInfo(e.target.checked));
  };

  const validateForm = () => {
    const errors = {};

    // Required fields validation
    if (!contactForm.name.trim()) {
      errors.name = "Name is required";
    }
    if (!contactForm.phone.trim()) {
      errors.phone = "Phone is required";
    }
    if (!contactForm.email.trim()) {
      errors.email = "Email is required";
    } else if (!validateEmail(contactForm.email)) {
      errors.email = "Please enter a valid email address";
    }
    if (!contactForm.address1.trim()) {
      errors.address1 = "Address is required";
    }
    if (!contactForm.city.trim()) {
      errors.city = "City is required";
    }
    if (!contactForm.state) {
      errors.state = "State is required";
    }
    if (!contactForm.postalCode.trim()) {
      errors.postalCode = "Postal code is required";
    }

    // Agent fields (required when checkbox is checked)
    if (showAgentInfo) {
      if (!agentForm.agentName.trim()) {
        errors.agentName = "Agent name is required";
      }
      if (!agentForm.agentPhone.trim()) {
        errors.agentPhone = "Agent phone is required";
      }
      if (!agentForm.agentEmail.trim()) {
        errors.agentEmail = "Agent email is required";
      } else if (!validateEmail(agentForm.agentEmail)) {
        errors.agentEmail = "Please enter a valid email address";
      }
    }

    dispatch(setFormErrors(errors));
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    const isValid = validateForm();
    if (isValid) {
      // Call API to fetch property details and move to Step 2
      dispatch(
        fetchPropertyDetails({
          schedulingHash: uniqueId,
          address: fullAddress,
        })
      );
    }
  };

  return (
    <div className="public-client-booking-form-container">
      <div className="public-client-booking-step-form">
        {/* Contact Section */}
        <div className="public-client-booking-form-section">
          <h3 className="public-client-booking-section-title">Contact</h3>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Name <span className="public-client-booking-required">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter your full name"
                  value={contactForm.name}
                  onChange={(e) => handleContactChange("name", e.target.value)}
                  isInvalid={!!formErrors.name}
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.name}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Phone{" "}
                  <span className="public-client-booking-required">*</span>
                </Form.Label>
                <Form.Control
                  type="tel"
                  placeholder="(123) 456-7890"
                  value={contactForm.phone}
                  onChange={(e) => handleContactChange("phone", e.target.value)}
                  isInvalid={!!formErrors.phone}
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.phone}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>
              Email <span className="public-client-booking-required">*</span>
            </Form.Label>
            <Form.Control
              type="email"
              placeholder="example@email.com"
              value={contactForm.email}
              onChange={(e) => handleContactChange("email", e.target.value)}
              isInvalid={!!formErrors.email}
            />
            <Form.Control.Feedback type="invalid">
              {formErrors.email}
            </Form.Control.Feedback>
          </Form.Group>
        </div>

        {/* Address Section */}
        <div className="public-client-booking-form-section">
          <h3 className="public-client-booking-section-title">Address</h3>

          <Form.Group className="mb-3">
            <Form.Label>
              Address 1{" "}
              <span className="public-client-booking-required">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Street address"
              value={contactForm.address1}
              onChange={(e) => handleContactChange("address1", e.target.value)}
              isInvalid={!!formErrors.address1}
            />
            <Form.Control.Feedback type="invalid">
              {formErrors.address1}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Address 2</Form.Label>
            <Form.Control
              type="text"
              placeholder="Apt, suite, unit, etc. (optional)"
              value={contactForm.address2}
              onChange={(e) => handleContactChange("address2", e.target.value)}
            />
          </Form.Group>

          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>
                  City <span className="public-client-booking-required">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="City"
                  value={contactForm.city}
                  onChange={(e) => handleContactChange("city", e.target.value)}
                  isInvalid={!!formErrors.city}
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.city}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>
                  State{" "}
                  <span className="public-client-booking-required">*</span>
                </Form.Label>
                <Form.Select
                  value={contactForm.state}
                  onChange={(e) => handleContactChange("state", e.target.value)}
                  isInvalid={!!formErrors.state}
                >
                  <option value="">Select State</option>
                  {US_STATES.map((state) => (
                    <option key={state.value} value={state.value}>
                      {state.label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {formErrors.state}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Postal Code{" "}
                  <span className="public-client-booking-required">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="ZIP code"
                  value={contactForm.postalCode}
                  onChange={(e) =>
                    handleContactChange("postalCode", e.target.value)
                  }
                  isInvalid={!!formErrors.postalCode}
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.postalCode}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        </div>

        {/* Agent Information Section */}
        <div className="public-client-booking-form-section">
          <Form.Check
            type="checkbox"
            id="add-agent-info"
            label="Add Agent Information (Optional)"
            checked={showAgentInfo}
            onChange={handleAgentCheckbox}
            className="public-client-booking-agent-checkbox"
          />
          <p className="public-client-booking-agent-hint">
            If an agent is involved, add their details to help with
            coordination.
          </p>

          {showAgentInfo && (
            <div className="public-client-booking-agent-fields">
              <h4 className="public-client-booking-subsection-title">Agent</h4>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Agent Name{" "}
                      <span className="public-client-booking-required">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Agent's full name"
                      value={agentForm.agentName}
                      onChange={(e) =>
                        handleAgentChange("agentName", e.target.value)
                      }
                      isInvalid={!!formErrors.agentName}
                    />
                    <Form.Control.Feedback type="invalid">
                      {formErrors.agentName}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Agent Phone{" "}
                      <span className="public-client-booking-required">*</span>
                    </Form.Label>
                    <Form.Control
                      type="tel"
                      placeholder="(123) 456-7890"
                      value={agentForm.agentPhone}
                      onChange={(e) =>
                        handleAgentChange("agentPhone", e.target.value)
                      }
                      isInvalid={!!formErrors.agentPhone}
                    />
                    <Form.Control.Feedback type="invalid">
                      {formErrors.agentPhone}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group className="mb-3">
                <Form.Label>
                  Agent Email{" "}
                  <span className="public-client-booking-required">*</span>
                </Form.Label>
                <Form.Control
                  type="email"
                  placeholder="example@email.com"
                  value={agentForm.agentEmail}
                  onChange={(e) =>
                    handleAgentChange("agentEmail", e.target.value)
                  }
                  isInvalid={!!formErrors.agentEmail}
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.agentEmail}
                </Form.Control.Feedback>
              </Form.Group>
            </div>
          )}
        </div>

        {/* Action Button */}
        <div className="public-client-booking-form-actions">
          <Button
            variant="primary"
            className="public-client-booking-next-btn"
            onClick={handleNext}
            disabled={propertyLoading}
          >
            {propertyLoading ? "Loading..." : "Next"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default StepContactAddress;
